import { MapPin, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CvUpload } from '@/components/profile/cv-upload';
import { SkillsSection } from '@/components/profile/skills-section';
import { ExperienceSection } from '@/components/profile/experience-section';
import { useMyProfile, type WorkExperience } from '@/hooks/use-profile';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';

export function ProfilePage() {
  const t = useT();
  const { data, isLoading } = useMyProfile();
  const queryClient = useQueryClient();
  const profile = data?.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('profile.title')}</h1>
        <Button
          size="sm"
          variant="outline"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['my-profile'] })}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          {t('common.refresh')}
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('profile.loading')}
        </div>
      ) : !profile ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-dashed p-8 text-center">
            <h2 className="text-lg font-semibold">{t('profile.welcome')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t('profile.welcomeHint')}</p>
          </div>
          <CvUpload />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Profile Header */}
          <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">{profile.name}</h2>
                {profile.email && <p className="text-sm text-muted-foreground">{profile.email}</p>}
                <div className="mt-2 flex items-center gap-2">
                  {profile.location && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {profile.location}
                    </span>
                  )}
                  {profile.seniority && (
                    <Badge variant="outline" className="text-xs capitalize">
                      {profile.seniority}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {profile.summary && (
              <p className="mt-3 text-sm text-muted-foreground">{profile.summary}</p>
            )}
          </div>

          {/* CV Upload */}
          <CvUpload />

          {/* Skills */}
          <SkillsSection skills={profile.skills ?? []} />

          {/* Work Experience */}
          <ExperienceSection
            experience={(profile.details?.workExperience ?? []) as WorkExperience[]}
          />

          {/* Education */}
          {profile.details?.education && profile.details.education.length > 0 && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-sm font-semibold">Education</h3>
              <div className="space-y-2">
                {profile.details.education.map((edu, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium">{edu.degree}</p>
                    <p className="text-xs text-muted-foreground">
                      {edu.institution} {edu.year ? `(${edu.year})` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {profile.details?.certifications && profile.details.certifications.length > 0 && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-sm font-semibold">Certifications</h3>
              <div className="flex flex-wrap gap-2">
                {profile.details.certifications.map((cert, i) => (
                  <Badge key={i} variant="outline">
                    {cert}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
